// La PARTIE en ligne (lot 4b, Task 9) : remplace le stub de T8. Le serveur est l'AUTORITÉ
// (il simule à 30 Hz et diffuse un snapshot complet 15/s) ; ce module ne fait qu'AFFICHER
// et ENVOYER les inputs. Il n'avance JAMAIS la simulation lui-même : entre deux snapshots,
// il interpole l'état précédent vers l'état courant avec le render EXISTANT (client/render.ts,
// jamais refactoré ici). L'identité (youAre, pseudos) et le monde (rows/seed/mode/moines)
// viennent du message `start` ; tout le reste (snapshot/pause/resume/gameover/aborted) est
// relayé par client/online-ui.ts via onMsg (la socket, elle, appartient au lobby).
//
// SÉCURITÉ : tout texte d'origine serveur/utilisateur (pseudos, nom de carte, raison
// d'abandon) est peint via fillText (canvas) ou drawCenterScreen (qui utilise fillText) ;
// JAMAIS d'innerHTML. Textes en français, sans tiret cadratin.
import { levelFromAscii } from '../shared/level'
import type { GameModeNet, ServerMsg } from '../shared/net-types'
import { toWorld } from '../shared/worlds'
import { decorGrid } from '../shared/decor'
import { type GameState, createGame, deserialize, orRestant } from '../shared/state'
import { estImmerge } from '../shared/sim'
import { COLS, type ItemKind, type PlayerInput, ROWS, toMonkSpeed } from '../shared/types'
import { apexPing, isMuted, playEvents, startAmbiance, startMusic, stopAmbiance, stopMusic, toggleMute } from './audio'
import { bounceVariant } from './bounce-variant'
import { createKeyboard } from './input'
import type { Net } from './net'
import { isTouch } from './platform'
import { DIG_ANIM_TICKS, FLASH_MS, type RenderCtx, type TazeFlash, drawTazeFlashes, render, setRunnerLooks } from './render'
import { hueIndex, skinIndex } from '../shared/skins'
import { titleLabel } from './loadout'
import { formatTime } from './format-time'
import { drawCenterScreen } from './screens'
import { createTouchControls } from './touch'

/** Le message `start` de ServerMsg (monde + identité de la partie). */
export type StartMsg = Extract<ServerMsg, { type: 'start' }>

export interface OnlineGameOpts {
  canvas: HTMLCanvasElement
  sheet: HTMLImageElement
  /** Socket ouverte (appartient au lobby) : la partie s'en sert pour input et leave_room. */
  net: Net
  /** Le message `start` reçu (le lobby vient de le recevoir et délègue). */
  start: StartMsg
  /** Retour au lobby (fin de partie confirmée par un tap/Entrée/Échap sur l'écran final). */
  onDone: () => void
}

/** Contrat consommé par online-ui.ts : recevoir les messages de partie et tout arrêter. */
export interface OnlineGameHandle {
  /** Le lobby relaie ici snapshot/pause/resume/gameover/aborted (la socket est à lui). */
  onMsg(msg: ServerMsg): void
  /** Arrêt inconditionnel : raf, interval d'input et écouteurs détachés (idempotent). */
  stop(): void
}

const TS = 48                 // une case terrain à l'écran (SCALE 3 × 16), miroir de render.ts
const HUD_H = TS              // bandeau du bas = une case de haut
const HUD_BG = '#1a1633'
const HUD_FG = '#f0e9d2'
const DIM = '#7a7599'
// Couleurs COSMÉTIQUES des joueurs (base N) : J1 bleu, J2 vert, J3 orange. Utilisées pour
// teinter les étiquettes du HUD à 3 joueurs et plus (à 2 joueurs, HUD historique inchangé).
const RUNNER_COLORS = ['#3b82f6', '#22c55e', '#f97316']
// Libellé du bandeau pour l'objet porté du joueur LOCAL (audit 16/07 : le multi n'annonçait que le
// tazer). Un ItemKind non couvert lèverait à la compilation, comme ITEM_SPRITES côté render.
const ITEM_LABEL: Record<ItemKind, string> = { bomb: 'BOMBE', jackhammer: 'MARTEAU', tazer: 'TAZER', key: 'CLÉ', rope: 'CORDE', appat: 'APPÂT', potion: 'POTION', deguisement: 'MASQUE', bandelette: 'BANDELETTE', meute: 'MEUTE', fantome: 'FANTÔME' }
const FONT = 'ui-monospace, Menlo, monospace'
/** Intervalle entre deux snapshots serveur (15/s = tous les 2 ticks) : borne l'interpolation. */
const SNAP_MS = 1000 / 15
/** Cadence d'envoi des inputs au serveur (ms) : ~30/s, modèle « dernier input connu ». */
const INPUT_MS = 33


/** Le message `gameover` de ServerMsg (vainqueur, ors, durée, contexte campagne éventuel). */
export type GameOverMsg = Extract<ServerMsg, { type: 'gameover' }>

/**
 * Contexte de partie nécessaire à l'écran de fin, EXTRAIT du scope de la partie pour rendre
 * buildGameOver PUR (donc testable, cf. tests/gameover.test.ts) : mode, pseudos de TOUS les
 * joueurs (start.players), mon index (youAre) et le signal de survie (lives configuré > 0 ET
 * au moins un coureur à 0 vie dans le dernier état reçu = élimination effective).
 */
export interface GameOverCtx {
  mode: GameModeNet
  players: string[]
  youAre: number
  survival: boolean
  eliminated: boolean
}

/**
 * Écran final versus/coop à partir du gameover (formulations FR, sans tiret cadratin). Fonction
 * PURE (aucun DOM, aucun état de module hors formatTime). BACK-COMPAT 2 joueurs STRICTE : quand
 * players.length === 2, les libellés sont EXACTEMENT ceux d'avant la base N ; la généralisation
 * à 3 joueurs et plus vit dans une branche séparée (classements de tous les joueurs). Les pseudos
 * finissent peints par fillText (jamais innerHTML), donc sûrs même piégés.
 */
export function buildGameOver(msg: GameOverMsg, ctx: GameOverCtx): { title: string; detail: string } {
  const { mode, players, youAre } = ctx
  const n = players.length
  if (mode === 'coop') {
    // Coop : le gameover ne survient QUE sur une victoire commune (l'abandon coop passe par
    // 'aborted'). On célèbre le niveau réussi ensemble. Identique à 2 comme à N.
    return { title: 'BRAVO !', detail: `Niveau réussi ensemble en ${formatTime(msg.durationMs)}` }
  }

  // ---- 2 joueurs : écran IDENTIQUE à l'existant (aucun libellé ne bouge) --------------
  if (n === 2) {
    const otherIdx = youAre === 0 ? 1 : 0
    if (msg.campaign) {
      const wins = msg.campaign.wins
      const myWins = wins[youAre] ?? 0
      const otherWins = wins[otherIdx] ?? 0
      if (msg.reason === 'abandon') {
        if (msg.winner === youAre) {
          return { title: 'Campagne gagnée !', detail: `${players[otherIdx]} a abandonné (${myWins} manches à ${otherWins})` }
        }
        return { title: 'Tu as abandonné', detail: 'Retour au salon' }
      }
      if (msg.winner === youAre) return { title: 'Tu remportes la campagne !', detail: `${myWins} manches à ${otherWins}` }
      const champ = players[msg.winner ?? 0] ?? 'Adversaire'
      return { title: 'Campagne perdue...', detail: `${champ} l'emporte ${otherWins} à ${myWins}` }
    }
    if (ctx.survival && ctx.eliminated && msg.reason !== 'abandon') {
      if (msg.winner === youAre) {
        const loser = players[otherIdx]
        return { title: 'Tu as gagné !', detail: `${loser} éliminé` }
      }
      const winnerName = players[msg.winner ?? 0] ?? 'Adversaire'
      return { title: 'Éliminé !', detail: `${winnerName} l'emporte` }
    }
    const mine = msg.golds[youAre] ?? 0
    const other = msg.golds[otherIdx] ?? 0
    if (msg.reason === 'abandon') {
      if (msg.winner === youAre) return { title: 'Tu as gagné !', detail: `${players[otherIdx]} a abandonné` }
      return { title: 'Tu as abandonné', detail: 'Retour au salon' }
    }
    if (msg.winner === null) return { title: 'Égalité !', detail: `${mine} à ${other}` }
    if (msg.winner === youAre) return { title: 'Tu as gagné !', detail: `${mine} pièces à ${other}` }
    const winnerName = players[msg.winner] ?? 'Adversaire'
    return { title: 'Perdu...', detail: `${winnerName} gagne ${msg.golds[msg.winner] ?? 0} à ${mine}` }
  }

  // ---- 3 joueurs et plus (base N) ----------------------------------------------------
  // Classement DÉCROISSANT de tous les joueurs (or ou manches), réutilisé dans plusieurs détails.
  const rankBy = (vals: number[]): string =>
    players
      .map((p, i) => ({ p, v: vals[i] ?? 0 }))
      .sort((a, b) => b.v - a.v)
      .map((e) => `${e.p} ${e.v}`)
      .join(' · ')
  const champ = players[msg.winner ?? 0] ?? 'Joueur'
  if (msg.campaign) {
    if (msg.reason === 'abandon') {
      // Forfait de campagne : le partant n'est plus « l'autre » unique (N), message générique.
      if (msg.winner === youAre) return { title: 'Campagne gagnée !', detail: 'Un joueur a abandonné' }
      return { title: 'Tu as abandonné', detail: 'Retour au salon' }
    }
    const winsRank = rankBy(msg.campaign.wins)
    if (msg.winner === youAre) return { title: 'Tu remportes la campagne !', detail: winsRank }
    return { title: 'Campagne perdue...', detail: `${champ} remporte la campagne. ${winsRank}` }
  }
  if (ctx.survival && ctx.eliminated && msg.reason !== 'abandon') {
    if (msg.winner === youAre) return { title: 'Tu as gagné !', detail: 'Dernier survivant !' }
    return { title: 'Éliminé !', detail: `${champ} l'emporte` }
  }
  const goldRank = rankBy(msg.golds)
  if (msg.reason === 'abandon') {
    if (msg.winner === youAre) return { title: 'Tu as gagné !', detail: 'Un joueur a abandonné' }
    return { title: 'Tu as abandonné', detail: 'Retour au salon' }
  }
  if (msg.winner === null) return { title: 'Égalité !', detail: goldRank }
  if (msg.winner === youAre) return { title: 'Tu as gagné !', detail: goldRank }
  return { title: 'Perdu...', detail: `${champ} gagne. ${goldRank}` }
}

export function startOnlineGame(opts: OnlineGameOpts): OnlineGameHandle {
  const ctx = opts.canvas.getContext('2d')!
  ctx.imageSmoothingEnabled = false
  const rc: RenderCtx = { ctx, sheet: opts.sheet }
  const { mode, players, youAre, levelName } = opts.start
  // Skins (19/07) : apparences par coureur reçues du serveur (anti-jumeaux déjà appliqué),
  // résolues en indices de feuille pour le render. `?? []` : tolérance à un vieux serveur
  // pendant un déploiement glissant (repli = prefs locales, comme avant). pet/title (C7) :
  // transportés TELS QUELS (loot_id) — `pet` pilote le familier par coureur (render.ts), `title`
  // (aussi lu depuis `looks` ci-dessous) s'affiche à l'écran de fin. Cosmétique pur, hors sim/hash.
  const looks = opts.start.looks ?? []
  setRunnerLooks(looks.map((l) => ({ skin: skinIndex(l.skin), hue: hueIndex(l.hue), pet: l.pet, title: l.title, outfit: l.outfit, hat: l.hat, pickaxe: l.pickaxe })))
  // Monde (thème) du niveau, NORMALISÉ à la frontière protocole : un `start.world` inconnu
  // (vieux/futur serveur) retombe sur classique plutôt que de peindre un fond/tuile undefined.
  const world = toWorld(opts.start.world)
  // AUDIT 16/07 : le multi n'avait AUCUNE ambiance sonore de biome, alors que le solo en a une depuis
  // le pack « game feel » — la même carte sonnait vide à deux et vivante en solo. Même appel, même
  // monde ; coupée au teardown comme en solo (stopAmbiance y est idempotent).
  startAmbiance(world)
  startMusic(world) // musique de fond du biome (miroir du solo ; idempotente si le biome ne change pas)
  // Décor (couche cosmétique) du niveau, parsé une fois depuis le `start` (hors sim/hash).
  const decor = decorGrid(opts.start.decor)
  // Contexte campagne (coop enchaînée) : présent quand le salon porte une campagne. On l'affiche
  // au décompte (nom + progression) et dans le HUD (progression compacte). Sans tiret cadratin
  // (règle projet) : séparateur « · », comme le reste de l'UI. Un nouveau niveau = un NOUVEAU
  // `start` (donc un nouveau startOnlineGame), ces constantes reflètent toujours le niveau courant.
  const campaign = opts.start.campaign ?? null
  // Campagne VERSUS : le score de manches (J1 - J2 AVANT ce niveau) accompagne la progression,
  // au décompte comme dans le HUD. En coop il n'y a pas de manches (victoire commune), wins = [0,0].
  const versusCampaign = campaign !== null && mode === 'versus'
  const campaignLevel = campaign ? `Niveau ${campaign.index + 1}/${campaign.total}` : null
  const campaignLabel = campaign
    ? versusCampaign
      ? `${campaign.name} · ${campaignLevel} · Manches ${campaign.wins[0]} - ${campaign.wins[1]}`
      : `${campaign.name} · ${campaignLevel}`
    : null

  // État d'affichage initial : MÊME création que le serveur (level/seed/mode/moines), pour
  // que le plateau montré pendant le compte à rebours colle exactement au 1er snapshot.
  const level = levelFromAscii(levelName, opts.start.rows)
  let curState: GameState = createGame(level, {
    mode,
    seed: opts.start.seed,
    monksEnabled: opts.start.monks,
    // Vitesse des moines (Task 3), NORMALISÉE à la frontière protocole (serveur ancien sans le
    // champ -> 'normal'). Colle l'état d'affichage du décompte au 1er snapshot serveur, et de
    // toute façon chaque snapshot reçu (deserialize) réimpose monkSpeed : jamais de divergence.
    monkSpeed: toMonkSpeed(opts.start.monkSpeed),
    // IA maligne : normalisée de même (serveur ancien sans le champ -> false = comportement
    // historique) ; chaque snapshot deserialize réimpose ensuite smartMonks.
    smartMonks: opts.start.smartMonks ?? false,
    monkPowers: opts.start.monkPowers ?? false,
    // Dash (Lot D) : capacité de la partie ; chaque snapshot deserialize la réimpose ensuite.
    dashEnabled: opts.start.dashEnabled ?? false,
  })
  let prevState: GameState | null = null
  let lastSnapAt = performance.now()
  // Compte à rebours dérivé de countdownMs + performance.now(). countdownMs 0 (reconnexion) :
  // pas de décompte, les inputs partent tout de suite.
  const countdownEndsAt = performance.now() + opts.start.countdownMs

  // Écrans transitoires. Un seul est visible à la fois (priorité : fin > pause > décompte >
  // confirmation d'abandon). paused.until = instant local de fin de la fenêtre de reprise.
  let paused: { pseudo: string; until: number } | null = null
  let ended: { title: string; detail: string } | null = null
  // Bandeau d'événement (mode survie) : texte affiché brièvement à l'annonce d'un hazard.
  let hazardBanner: { text: string; until: number } | null = null
  let confirming = false // overlay « Abandonner ? » (le jeu CONTINUE derrière)
  let leaving = false    // leave_room envoyé : on n'envoie plus d'input en attendant la fin
  let stopped = false
  let raf = 0
  let inputTimer: ReturnType<typeof setInterval> | null = null
  // Éclairs de tir tazer (transitoires) : remplis à réception des events 'taze' d'un snapshot,
  // purgés (> FLASH_MS) et peints dans frame. Aucun raf/interval propre : le raf existant les porte.
  const flashes: TazeFlash[] = []
  // Anim de creusage (transitoire, cosmétique) : par coureur, tick jusqu'auquel jouer la frame
  // `dig`, armé sur l'event digAnim d'un snapshot. Les ticks serveur sont monotones dans une
  // partie et digUntil part vide à l'ouverture -> aucune échéance périmée à purger (contrairement
  // à solo qui réutilise la même closure entre niveaux).
  const digUntil: number[] = []
  // Habillage du saut trampoline (18/07, variantes aléatoires) : par coureur, [tick du rebond,
  // ampleur en su] armés sur l'event bounce d'un snapshot (même logique de vie que digUntil).
  const bounceAnim: [number, number][] = []

  // --- Entrées : clavier + tactile fusionnés (réplique de solo.ts) --------------
  const kb = createKeyboard()
  kb.attach()
  // En ligne, le bouton ⏸ ne met PAS en pause (pas de pause volontaire) : il déclenche la
  // demande d'abandon, exactement comme la touche Échap.
  const touch = isTouch
    ? createTouchControls({
        onPause() { onPauseButton() },
        onMute() { toggleMute(); touch!.setMuted(isMuted()) },
      })
    : null
  touch?.attach()
  touch?.setMuted(isMuted())
  function mergedInput(): PlayerInput {
    const a = kb.current()
    if (!touch) return a
    const b = touch.current()
    return {
      left: a.left || b.left, right: a.right || b.right, up: a.up || b.up, down: a.down || b.down,
      digL: a.digL || b.digL, digR: a.digR || b.digR, use: a.use || b.use, dash: a.dash || b.dash,
      useDir: a.useDir ?? b.useDir, // sens d'usage (#5) : clavier null -> tactile ; ni l'un ni l'autre -> null
    }
  }

  // --- Réception des messages de partie (relayés par online-ui) -----------------
  function onMsg(msg: ServerMsg): void {
    if (stopped) return
    switch (msg.type) {
      case 'snapshot':
        prevState = curState
        curState = deserialize(msg.state)
        lastSnapAt = performance.now()
        playEvents(msg.events) // sons du jeu (or ramassé, creuse, explosion, victoire, hazard...)
        touch?.setItem(curState.runners[opts.start.youAre]?.item ?? null) // icône du bouton OBJET = objet équipé (16/07)
        touch?.setOutil(curState.runners[opts.start.youAre]?.maillet ?? false) // 06/08 : pioche <-> maillet sur les boutons
        {
          // NAGE (06/08) : la diagonale du joystick, d'après la TUILE du coureur — jamais d'après
          // son souffle, que les sources de bulles rechargent au plein en pleine immersion.
          const moi = curState.runners[opts.start.youAre]
          touch?.setNage(!!moi && estImmerge(curState, moi))
        }
        // Événement aléatoire (mode survie) : bandeau bref annonçant le danger.
        for (const e of msg.events) if (e.type === 'hazard') hazardBanner = { text: hazardLabel(e.kind), until: performance.now() + 1800 }
        // Éclairs de tir tazer : armés depuis les events du snapshot (from/to en cases), purgés à FLASH_MS.
        for (const e of msg.events) if (e.type === 'taze') flashes.push({ from: e.from, to: e.to, bornAt: performance.now() })
        // Anim de creusage : le coureur qui a creusé joue la frame `dig` DIG_ANIM_TICKS ticks (échéance en ticks de sim).
        for (const e of msg.events) if (e.type === 'digAnim') digUntil[e.runner] = curState.tick + DIG_ANIM_TICKS
        // Rebond : mémorise [tick, ampleur] pour l'habillage du saut (4 variantes aléatoires) ;
        // variante « propulseur » sur un vrai trampoline (>= 24 su) = ping programmé à l'apex.
        for (const e of msg.events) if (e.type === 'bounce') {
          const rn = curState.runners[e.runner]
          if (rn) {
            bounceAnim[e.runner] = [curState.tick, rn.bounce]
            if (rn.bounce >= 24 && bounceVariant(curState.tick, e.runner) === 3) apexPing(rn.bounce / 2 / 30)
          }
        }
        return
      case 'pause':
        // deadlineMs = DURÉE de la fenêtre de reprise (le serveur envoie pauseMs), pas un
        // instant : on la convertit en échéance locale pour le décompte affiché.
        paused = { pseudo: msg.pseudo, until: performance.now() + msg.deadlineMs }
        return
      case 'resume':
        paused = null
        return
      case 'gameover':
        // survival/eliminated : signal de survie extrait ICI (dernier état reçu) pour garder
        // buildGameOver pure. survie active = lives configuré > 0 ; élimination = un coureur à 0 vie.
        ended = buildGameOver(msg, {
          mode,
          players,
          youAre,
          survival: curState.lives > 0,
          eliminated: curState.runners.some((r) => r.lives <= 0),
        })
        paused = null
        confirming = false
        return
      case 'aborted':
        ended = { title: 'Partie terminée', detail: msg.reason }
        paused = null
        confirming = false
        return
      default:
        // online/rooms/room_created/error/start : affaire du lobby (online-ui), pas ici.
        return
    }
  }

  // --- Rendu --------------------------------------------------------------------
  function drawHud(s: GameState): void {
    const top = ROWS * TS
    ctx.fillStyle = HUD_BG
    ctx.fillRect(0, top, COLS * TS, HUD_H)
    const midY = top + HUD_H / 2
    ctx.font = `24px ${FONT}`
    ctx.textBaseline = 'middle'
    ctx.fillStyle = HUD_FG
    const time = formatTime((s.tick * 1000) / 30)
    const n = s.runners.length
    if (mode === 'versus' && n === 2) {
      // 2 joueurs : disposition gauche/droite HISTORIQUE (inchangée, back-compat stricte).
      const g0 = s.runners[0]?.gold ?? 0
      const g1 = s.runners[1]?.gold ?? 0
      // Mode survie (s.lives>0) : vies restantes de chaque joueur accolées à son cluster.
      const hp0 = s.lives > 0 ? `  ♥${s.runners[0]?.lives ?? 0}` : ''
      const hp1 = s.lives > 0 ? `♥${s.runners[1]?.lives ?? 0}  ` : ''
      // Cles de chaque joueur (tuiles P3 T2) : n'apparait que si le joueur en porte (zero impact
      // sur les cartes sans cle). Symetrique : accolee apres l'or a gauche (J1), avant a droite (J2).
      const k0 = (s.runners[0]?.keys ?? 0) > 0 ? ` CLÉ ${s.runners[0]!.keys}` : ''
      const k1 = (s.runners[1]?.keys ?? 0) > 0 ? `CLÉ ${s.runners[1]!.keys} ` : ''
      ctx.textAlign = 'left'
      ctx.fillText(`${players[0]}  ●${g0}${k0}${hp0}`, 16, midY)              // pseudo J1 (fillText : sûr)
      ctx.textAlign = 'center'
      // Versus campagne : score de manches (J1-J2) accolé au temps ; versus mono : temps seul.
      ctx.fillText(versusCampaign ? `${campaign!.wins[0]}-${campaign!.wins[1]}  ${time}` : time, (COLS * TS) / 2, midY)
      ctx.textAlign = 'right'
      ctx.fillText(`${hp1}${k1}●${g1}  ${players[1]}`, COLS * TS - 16, midY)  // pseudo J2 (fillText : sûr)
    } else if (mode === 'versus') {
      // 3 joueurs et plus (base N) : une étiquette COLORÉE par joueur, répartie régulièrement sur
      // la largeur. Format « pseudo ●or (♥vies en survie) ». Le chrono central cède la place aux
      // étiquettes ; ma charge de tazer est accolée à MA propre étiquette (bloc central désactivé).
      ctx.textAlign = 'center'
      for (let i = 0; i < n; i++) {
        const g = s.runners[i]?.gold ?? 0
        const hp = s.lives > 0 ? ` ♥${s.runners[i]?.lives ?? 0}` : ''
        const tz = i === youAre && s.runners[i]?.item === 'tazer' ? ` z${s.runners[i]?.charges ?? 0}` : ''
        const kk = (s.runners[i]?.keys ?? 0) > 0 ? ` CLÉ${s.runners[i]!.keys}` : '' // cles du joueur (tuiles P3 T2)
        ctx.fillStyle = RUNNER_COLORS[i] ?? HUD_FG
        ctx.fillText(`${players[i] ?? ''} ●${g}${hp}${tz}${kk}`, ((i + 0.5) / n) * COLS * TS, midY) // pseudo (fillText : sûr)
      }
      ctx.fillStyle = HUD_FG
    } else {
      ctx.textAlign = 'left'
      // Coop : or restant global + cles du coureur local (tuiles P3 T2, s'il en porte).
      const kc = (s.runners[youAre]?.keys ?? 0) > 0 ? `     CLÉ ${s.runners[youAre]!.keys}` : ''
      // Même règle qu'en solo : `golds` n'est que l'or AU SOL. En coop, une pièce volée par un
      // moine faisait tomber ce compteur à 0 alors que la sortie refusait de s'ouvrir.
      ctx.fillText(`OR restant ${orRestant(s)}${kc}`, 16, midY)
      ctx.textAlign = 'center'
      ctx.fillText(time, (COLS * TS) / 2, midY)
      ctx.textAlign = 'right'
      // Campagne coop : progression « Niveau X/total » (compacte) ; sinon nom de carte. fillText : sûr.
      ctx.fillText(campaignLevel ?? levelName, COLS * TS - 16, midY)
    }
    // Bloc du coureur LOCAL (via youAre) : objet porté puis état du dash, dans la zone libre à
    // droite du chrono central (sûre en versus 2 joueurs comme en coop : les clusters latéraux n'y
    // arrivent pas). À 3 joueurs et plus, le centre porte une étiquette : on n'écrit rien ici (la
    // charge de tazer est déjà accolée à l'étiquette du joueur local).
    const meRunner = s.runners[youAre]
    if (!(mode === 'versus' && n >= 3)) {
      ctx.textAlign = 'left'
      let cursorX = (COLS * TS) / 2 + 80
      // AUDIT 16/07 : le multi n'affichait QUE le tazer, alors qu'on y ramasse aussi bombes, marteau
      // et corde — on portait un objet sans le savoir (le solo l'affiche dans son bandeau).
      if (meRunner?.item) {
        const charges = meRunner.item === 'tazer' || meRunner.item === 'rope' ? ` ${meRunner.charges}` : ''
        const txt = `${ITEM_LABEL[meRunner.item]}${charges}`
        ctx.fillStyle = '#ffd23f'
        ctx.fillText(txt, cursorX, midY)
        cursorX += ctx.measureText(txt).width + 28
      }
      // AUDIT 16/07 : le solo affiche « DASH prêt / ... », le multi non — on dashait à l'aveugle,
      // sans savoir si les 10 s de recharge étaient écoulées. Mêmes libellés, mêmes couleurs.
      if (s.dashEnabled) {
        const ready = (meRunner?.dashReadyAt ?? 0) <= s.tick
        ctx.fillStyle = ready ? '#5bd75b' : '#7a7599'
        ctx.fillText(`DASH ${ready ? 'prêt' : '...'}`, cursorX, midY)
      }
      ctx.fillStyle = HUD_FG
    }
    ctx.textAlign = 'left'
  }

  function drawEnd(): void {
    drawCenterScreen(ctx, ended!.title, ended!.detail)
    const W = ctx.canvas.width, H = ctx.canvas.height
    // TITRES ÉQUIPÉS (Mine C7) : chaque joueur qui porte un titre le montre aux AUTRES ici (l'écran
    // de fin est vu de tous). `looks` (start.looks) est dans le MÊME ordre que `players` ; on résout
    // le loot_id en libellé (préfixe « Titre : » retiré). Sobre : ligne omise si personne n'en porte.
    // Peint par fillText (jamais innerHTML) -> pseudos et libellés sûrs même piégés. Sans tiret cadratin.
    const titleLine = players
      .map((p, i) => { const t = titleLabel(looks[i]?.title ?? null); return t ? `${p} (${t})` : null })
      .filter((s): s is string => s !== null)
      .join(' · ')
    if (titleLine) {
      ctx.fillStyle = '#e0c060' // or doux : un galon discret, sans crier plus fort que le résultat
      ctx.font = `20px ${FONT}`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(titleLine, W / 2, H - 80)
    }
    ctx.fillStyle = DIM
    ctx.font = `22px ${FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(isTouch ? "Touche l'écran pour revenir au salon" : 'Entrée ou Échap pour revenir au salon', W / 2, H - 44)
    ctx.textAlign = 'left'
  }

  function drawPause(now: number): void {
    const sec = Math.max(0, Math.ceil((paused!.until - now) / 1000))
    drawCenterScreen(ctx, 'EN PAUSE', `${paused!.pseudo} a perdu la connexion, reprise sous ${sec} s`)
  }

  function drawCountdown(now: number): void {
    const n = Math.max(1, Math.ceil((countdownEndsAt - now) / 1000))
    // Campagne coop : « <nom> · Niveau X/total » sous le décompte ; sinon nom de carte.
    drawCenterScreen(ctx, String(n), campaignLabel ?? levelName)
  }

  function drawConfirm(): void {
    drawCenterScreen(
      ctx,
      'Abandonner la partie ?',
      isTouch ? "⏸ pour confirmer, touche l'écran pour continuer" : 'Échap pour confirmer, autre touche pour continuer',
    )
  }

  /** Libellé FR d'un événement aléatoire (mode survie). */
  function hazardLabel(kind: 'monks' | 'bombs' | 'collapse' | 'slick'): string {
    return kind === 'monks'
      ? 'Vague de moines !'
      : kind === 'bombs'
        ? 'Pluie de bombes !'
        : kind === 'collapse'
          ? 'Éboulement !'
          : 'Ça glisse !'
  }

  /** Bandeau d'alerte en haut de l'aire de jeu, le temps de l'annonce d'un événement. */
  function drawHazard(text: string): void {
    const W = ctx.canvas.width
    ctx.save()
    ctx.font = `bold 34px ${FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const w = ctx.measureText(text).width + 48
    const x = (W - w) / 2, y = TS * 1.2
    ctx.fillStyle = 'rgba(26, 22, 51, 0.85)'
    ctx.fillRect(x, y - 26, w, 52)
    ctx.strokeStyle = '#ff8a3c'
    ctx.lineWidth = 2
    ctx.strokeRect(x + 1, y - 25, w - 2, 50)
    ctx.fillStyle = '#ffd23f'
    ctx.fillText(text, W / 2, y)
    ctx.restore()
  }

  function frame(now: number): void {
    if (stopped) return
    const alpha = Math.min(1, (now - lastSnapAt) / SNAP_MS)
    const cam = render(rc, prevState, curState, alpha, world, decor, digUntil, youAre, bounceAnim) // caméra sur MON coureur (youAre)
    // Éclairs de tazer (transitoires) : purge des expirés puis rendu par-dessus le terrain, sous le HUD.
    while (flashes.length > 0 && now - flashes[0]!.bornAt > FLASH_MS) flashes.shift()
    drawTazeFlashes(rc, flashes, now, curState.cols, cam)
    drawHud(curState)
    // Bandeau d'événement (mode survie), pendant le jeu uniquement (pas sur un écran de fin).
    if (hazardBanner && now < hazardBanner.until && !ended) drawHazard(hazardBanner.text)
    // Priorité stricte des overlays : fin de partie > pause serveur > compte à rebours >
    // confirmation d'abandon (le décompte et la confirmation ne coexistent jamais avec une fin).
    if (ended) drawEnd()
    else if (paused) drawPause(now)
    else if (now < countdownEndsAt) drawCountdown(now)
    else if (confirming) drawConfirm()
    raf = requestAnimationFrame(frame)
  }

  // --- Entrées de fin / abandon -------------------------------------------------
  function onPauseButton(): void {
    if (stopped) return
    if (ended) { done(); return }
    if (paused) return
    if (confirming) doAbandon() // 2e appui sur ⏸ = confirmer
    else confirming = true      // 1er appui = demander
  }

  function doAbandon(): void {
    confirming = false
    leaving = true
    // Le serveur répond gameover (versus) ou aborted (coop), relayé à onMsg : c'est lui qui
    // posera l'écran de fin. Le jeu continue de s'afficher d'ici là (pas de pause volontaire).
    opts.net.send({ type: 'leave_room' })
  }

  function onKey(e: KeyboardEvent): void {
    if (stopped) return
    if (ended) {
      if (e.code === 'Enter' || e.code === 'NumpadEnter' || e.code === 'Escape') { e.preventDefault(); done() }
      return
    }
    if (confirming) {
      if (e.code === 'Escape') { e.preventDefault(); doAbandon() }
      else confirming = false // toute autre touche : on continue la partie
      return
    }
    if (paused) return
    if (e.code === 'Escape') { e.preventDefault(); confirming = true }
  }

  function onClick(e: MouseEvent): void {
    if (stopped) return
    if (ended) { e.preventDefault(); done(); return }
    if (confirming) { e.preventDefault(); confirming = false; return } // tap ailleurs = continuer
    // En jeu normal, un tap sur le canvas ne fait rien (les commandes sont les boutons tactiles).
  }

  function teardown(): void {
    if (stopped) return
    stopped = true
    setRunnerLooks(null) // retour aux prefs locales (solo/menu)
    cancelAnimationFrame(raf)
    if (inputTimer !== null) clearInterval(inputTimer)
    inputTimer = null
    stopAmbiance() // coupe l'ambiance sonore de fond en quittant la partie (miroir du solo)
    stopMusic() // coupe la musique de fond (arrête + déconnecte tous ses nœuds : zéro fuite)
    kb.detach()
    touch?.detach()
    removeEventListener('keydown', onKey)
    opts.canvas.removeEventListener('click', onClick)
  }

  function done(): void {
    if (stopped) return
    teardown()
    opts.onDone()
  }

  // Envoi des inputs : SEULEMENT après le compte à rebours et hors pause/fin/abandon.
  inputTimer = setInterval(() => {
    if (stopped) return
    if (performance.now() < countdownEndsAt) return
    if (paused || ended || leaving) return
    opts.net.send({ type: 'input', keys: mergedInput() })
  }, INPUT_MS)

  addEventListener('keydown', onKey)
  opts.canvas.addEventListener('click', onClick)
  raf = requestAnimationFrame(frame)
  return { onMsg, stop: teardown }
}
